import { useEffect, useMemo, useState, useCallback, useRef, createContext, useContext } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
    ReactFlow, Background, Controls, Position, Handle, MarkerType,
    useReactFlow, ReactFlowProvider, useNodesState, useEdgesState,
    BaseEdge, EdgeLabelRenderer, getSmoothStepPath, Panel, NodeToolbar,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
    X, Workflow, Loader2, AlertCircle, PhoneOff, Shuffle, ArrowDownNarrowWide, Pencil, Crosshair, Save, RotateCcw, ListChecks, ChevronDown,
} from 'lucide-react';
import { fetchFlow, fetchQuestions, saveFlow } from '../../../api/survey.js';
import { overlayDismiss } from '../../../lib/overlayDismiss';

/**
 * SurveyFlowEditorModal — 問卷流程 canvas(唯讀,Phase 1)
 *
 * Visual 完全照搬 qubby-survey-editor 的 FlowCanvas + QuestionNode 結構:
 *   - 寬 node(min 280px),含**完整 header band** + body + 選項列
 *   - 每選項是 cream 圓角列 + 圓形序號徽 + 右側**方形** source handle
 *   - 左側**圓形** target handle,未連線時 = accent(警告色)
 *   - 孤立節點(沒任何 in/out)= accent pulse 邊框警告
 *   - hover/selected 行為跟 Vue editor 對齊
 *
 * 色系全換 cream/ink/accent(原本 Vue 用 blue/green/purple):
 *   - choice header: accent gradient(terracotta)
 *   - open header: ink gradient(深色,編輯風)
 *   - option badge: accent-500 圓底白字
 *   - option handle: 方形 white + accent-500 border
 *   - target handle: 圓形 white + ink-700 border;未連線時 accent-500 border
 */

// ── 色彩 palette:每題給不同顏色,12 色循環(warm + cohesive,與 cream 背景搭)──
const NODE_PALETTE = [
    { base: '#c84a2c', dark: '#a83a1f' }, // terracotta(accent-500)
    { base: '#d97e2e', dark: '#b8651e' }, // burnt orange
    { base: '#d4a017', dark: '#a87f10' }, // mustard
    { base: '#7b8c4a', dark: '#5e7036' }, // olive
    { base: '#6e8c7a', dark: '#557060' }, // sage
    { base: '#3a8b95', dark: '#2a6e76' }, // teal
    { base: '#5e7a91', dark: '#476078' }, // slate
    { base: '#7c5e7a', dark: '#5e4860' }, // plum
    { base: '#8e3a5c', dark: '#6c2745' }, // wine
    { base: '#a55d2e', dark: '#7e4422' }, // rust
    { base: '#a88940', dark: '#856b30' }, // ochre
    { base: '#4e7a3e', dark: '#3a5e2e' }, // forest
];
function colorForIndex(i) {
    return NODE_PALETTE[((i || 0) - 1 + NODE_PALETTE.length) % NODE_PALETTE.length];
}

// ── Custom node components ───────────────────────────────────────

function QuestionNode({ id, data, selected }) {
    const { t } = useTranslation('wsSurvey');
    const { setCenter, getNodes } = useReactFlow();
    const isChoice = (data.questionType || 'choice') === 'choice';
    const isOpen = data.questionType === 'open';
    const opts = data.options || [];
    const truncate = (s, n = 80) => (s && s.length > n ? s.slice(0, n) + '…' : (s || t('node.noQuestionContent')));

    // 居中按鈕(對齊 Vue editor 的 handleCenterNode)
    const onCenter = (e) => {
        e.stopPropagation();
        const n = getNodes().find((x) => x.id === id);
        if (n?.position) {
            const w = n.measured?.width || 280;
            const h = n.measured?.height || 100;
            setCenter(n.position.x + w / 2, n.position.y + h / 2, { zoom: 1, duration: 400 });
        }
    };

    // 每題自己的顏色(從 data.colorBase/colorDark,buildRfGraph 在 build 時注入)
    const base = data.colorBase || '#c84a2c';
    const dark = data.colorDark || '#a83a1f';
    const borderColor = data.isIsolated ? base : (selected ? dark : '#e0d4ba');

    return (
        <>
        <NodeDeleteToolbar id={id} selected={selected} />
        <div
            data-type={data.questionType}
            data-isolated={data.isIsolated ? '1' : '0'}
            className="q-node bg-white rounded-lg overflow-hidden transition-all min-w-[280px] max-w-[340px] shadow-sm hover:shadow-md"
            style={{
                borderWidth: data.isIsolated || selected ? 3 : 2,
                borderStyle: 'solid',
                borderColor,
                boxShadow: data.isIsolated
                    ? `0 2px 8px ${base}4d, 0 0 0 3px ${base}1a`
                    : selected
                        ? `0 0 0 3px ${base}1f`
                        : undefined,
            }}
        >
            {/* ── HEADER band(對齊 Vue editor 的 question-node-header)── */}
            <div
                className="px-3 py-2 flex items-center justify-between text-cream-50"
                style={{
                    background: isOpen
                        ? 'linear-gradient(135deg, #3d362d 0%, #1c1a17 100%)'
                        : `linear-gradient(135deg, ${base} 0%, ${dark} 100%)`,
                }}
            >
                <span
                    className="font-mono text-[11px] tracking-[0.12em]"
                    style={{ fontWeight: 600 }}
                >
                    {data.displayLabel || `Q${data.displayOrder}`}
                </span>
                <button
                    onClick={onCenter}
                    title={t('node.centerView')}
                    className="inline-flex items-center justify-center w-5 h-5 rounded text-cream-50 bg-white/15 hover:bg-white/30 transition-all hover:scale-110"
                >
                    <Crosshair size={11} strokeWidth={2} />
                </button>
            </div>

            {/* ── BODY ── */}
            <div className="px-3 py-3 flex flex-col gap-2.5">
                <p
                    className="text-[12.5px] leading-relaxed text-ink-700 break-words m-0"
                    style={{ fontFamily: '"Fraunces", "Noto Serif TC", serif', fontWeight: 450 }}
                >
                    {truncate(data.questionText)}
                </p>

                {/* Options(僅 choice;每選項 cream 列 + 圓徽 + 右側方形 handle)*/}
                {isChoice && opts.length > 0 && (
                    <div className="flex flex-col gap-1.5 mt-0.5">
                        {opts.map((opt, idx) => (
                            <div
                                key={opt.id || idx}
                                className="opt-row relative flex items-center gap-2 pl-2 pr-5 py-1.5 bg-cream-100 rounded text-[11.5px] text-ink-700 transition-all hover:bg-cream-200 hover:pr-6"
                            >
                                <span
                                    className="inline-flex items-center justify-center w-[18px] h-[18px] text-cream-50 rounded-full text-[9.5px] tabular-nums flex-shrink-0"
                                    style={{ background: base, fontWeight: 600 }}
                                >
                                    {idx + 1}
                                </span>
                                <span className="flex-1 truncate">{opt.label}</span>

                                {/* 方形 source handle — 對齊 Vue editor option-handle,顏色跟 node */}
                                <Handle
                                    type="source"
                                    position={Position.Right}
                                    id={`option-${idx}`}
                                    className="!w-[14px] !h-[14px] !rounded-[3px] !bg-white !border-[3px] !right-[-8px]"
                                    style={{ top: '50%', transform: 'translateY(-50%)', borderColor: base }}
                                />
                            </div>
                        ))}
                    </div>
                )}

                {/* Open 題提示 */}
                {isOpen && (
                    <div className="flex items-center gap-1.5 px-2.5 py-2 bg-cream-100 border border-cream-300 rounded text-[11.5px] text-ink-600">
                        <Pencil size={12} strokeWidth={1.8} />
                        <span>{t('node.openAnswer')}</span>
                    </div>
                )}
            </div>

            {/* ── Connection handles ── */}
            {/* 圓形 target(左)— 未連線時 base 警告色 */}
            <Handle
                type="target"
                position={Position.Left}
                id="target"
                className="!w-[16px] !h-[16px] !rounded-full !bg-white !border-[3px] !left-[-8px]"
                style={{ borderColor: data.hasIncoming ? '#3d362d' : base }}
            />

            {/* open 題用單一 default source handle(方形,在右側中間)*/}
            {isOpen && (
                <Handle
                    type="source"
                    position={Position.Right}
                    id="default"
                    className="!w-[14px] !h-[14px] !rounded-[3px] !bg-white !border-[3px] !right-[-8px]"
                    style={{ borderColor: base }}
                />
            )}
        </div>
        </>
    );
}

function RandomGroupNode({ id, data, selected }) {
    const { t } = useTranslation('wsSurvey');
    const questions = data.questions || [];
    const selectCount = data.selectCount ?? 1;
    return (
        <>
        <GroupNodeToolbar id={id} selected={selected} />
        <div
            className={[
                'bg-white rounded-lg overflow-hidden transition-all min-w-[300px] max-w-[360px] shadow-sm',
                selected ? 'border-[3px] border-accent-700' : 'border-2 border-accent-300',
            ].join(' ')}
        >
            <Handle type="target" position={Position.Left} id="target" className="!w-[16px] !h-[16px] !rounded-full !bg-white !border-[3px] !border-ink-700 !left-[-8px]" style={{ top: 22 }} />
            {/* Header */}
            <div className="px-3 py-2 flex items-center justify-between bg-gradient-to-br from-accent-500 to-accent-700 text-cream-50">
                <div className="flex items-center gap-1.5">
                    <Shuffle size={11} strokeWidth={2} />
                    <span className="text-[12px]" style={{ fontFamily: '"Fraunces", "Noto Serif TC", serif', fontWeight: 500 }}>
                        {data.title || data.displayLabel || t('node.randomGroup')}
                    </span>
                </div>
                <span className="font-mono text-[9px] opacity-80 tracking-[0.14em]" style={{ fontWeight: 600 }}>
                    RANDOM
                </span>
            </div>
            {/* selectCount bar */}
            <div className="px-3 py-2 bg-accent-50 border-b border-accent-100 flex items-center justify-between">
                <span className="text-[11px] text-ink-600">{t('node.randomSelect')}</span>
                <span className="font-mono text-[12px] text-accent-700 tabular-nums" style={{ fontWeight: 600 }}>
                    {selectCount} <span className="text-ink-400">/</span> {t('node.questionCount', { count: questions.length })}
                </span>
            </div>
            {/* 內容:每題 + 選項預覽 */}
            <div className="px-3 py-2 flex flex-col gap-2">
                {questions.length === 0 && (
                    <p className="text-[10px] text-ink-400 italic">{t('node.noQuestionData')}</p>
                )}
                {questions.map((q, idx) => (
                    <div key={q.questionId || idx} className="rounded border border-cream-200 bg-cream-50/40 p-2">
                        <div className="flex items-baseline gap-1.5 mb-1">
                            <span className="font-mono text-[9px] text-accent-700 tabular-nums" style={{ fontWeight: 600 }}>
                                Q{idx + 1}
                            </span>
                            <p
                                className="text-[11px] text-ink-900 leading-snug line-clamp-2 flex-1"
                                style={{ fontFamily: '"Fraunces", "Noto Serif TC", serif', fontWeight: 450 }}
                            >
                                {q.question || q.questionText || t('node.unset')}
                            </p>
                        </div>
                        {(q.customOptions || []).slice(0, 4).map((opt, j) => (
                            <div key={j} className="flex items-center gap-1.5 px-1 py-0.5 text-[10px] text-ink-600">
                                <span className="inline-flex items-center justify-center w-[14px] h-[14px] bg-accent-500/70 text-cream-50 rounded-full text-[8px] tabular-nums flex-shrink-0" style={{ fontWeight: 600 }}>
                                    {j + 1}
                                </span>
                                <span className="truncate">{opt.label}</span>
                            </div>
                        ))}
                        {(q.customOptions || []).length > 4 && (
                            <div className="text-[9px] text-ink-400 mt-0.5">{t('node.moreOptions', { count: q.customOptions.length - 4 })}</div>
                        )}
                    </div>
                ))}
            </div>
            <Handle type="source" position={Position.Right} id="default" className="!w-[14px] !h-[14px] !rounded-[3px] !bg-white !border-[3px] !border-accent-500 !right-[-8px]" />
        </div>
        </>
    );
}

function ReorderGroupNode({ id, data, selected }) {
    const { t } = useTranslation('wsSurvey');
    const questions = data.questions || [];
    return (
        <>
        <GroupNodeToolbar id={id} selected={selected} />
        <div
            className={[
                'bg-white rounded-lg overflow-hidden transition-all min-w-[300px] max-w-[360px] shadow-sm',
                selected ? 'border-[3px] border-ink-900' : 'border-2 border-ink-300',
            ].join(' ')}
        >
            <Handle type="target" position={Position.Left} id="target" className="!w-[16px] !h-[16px] !rounded-full !bg-white !border-[3px] !border-ink-700 !left-[-8px]" style={{ top: 22 }} />
            {/* Header */}
            <div className="px-3 py-2 flex items-center justify-between bg-gradient-to-br from-ink-700 to-ink-900 text-cream-50">
                <div className="flex items-center gap-1.5">
                    <ArrowDownNarrowWide size={11} strokeWidth={2} />
                    <span className="text-[12px]" style={{ fontFamily: '"Fraunces", "Noto Serif TC", serif', fontWeight: 500 }}>
                        {data.title || data.displayLabel || t('node.reorderGroup')}
                    </span>
                </div>
                <span className="font-mono text-[9px] opacity-80 tracking-[0.14em]" style={{ fontWeight: 600 }}>
                    REORDER
                </span>
            </div>
            {/* Subtitle */}
            <div className="px-3 py-2 bg-ink-50/40 border-b border-cream-200">
                <div className="flex items-center justify-between mb-0.5">
                    <span className="text-[11px] text-ink-700">{t('node.askAllRandom')}</span>
                    <span className="font-mono text-[12px] text-ink-900 tabular-nums" style={{ fontWeight: 600 }}>
                        {t('node.questionCount', { count: questions.length })}
                    </span>
                </div>
                <p className="text-[10px] text-ink-400 italic">{t('node.reorderHint')}</p>
            </div>
            {/* 題目清單(只顯示題目文字,不展開選項)*/}
            <div className="px-3 py-2 flex flex-col gap-1.5">
                {questions.length === 0 && (
                    <p className="text-[10px] text-ink-400 italic">{t('node.noQuestionData')}</p>
                )}
                {questions.map((q, idx) => (
                    <div key={q.questionId || idx} className="flex items-baseline gap-1.5 px-1.5 py-1 rounded bg-cream-50/40 border border-cream-200">
                        <span className="font-mono text-[9px] text-ink-700 tabular-nums" style={{ fontWeight: 600 }}>
                            Q{idx + 1}
                        </span>
                        <p
                            className="text-[11px] text-ink-900 leading-snug line-clamp-2 flex-1"
                            style={{ fontFamily: '"Fraunces", "Noto Serif TC", serif', fontWeight: 450 }}
                        >
                            {q.question || q.questionText || t('node.unset')}
                        </p>
                    </div>
                ))}
            </div>
            {/* Footer note */}
            <div className="px-3 py-1.5 bg-cream-100/60 border-t border-cream-200">
                <p className="text-[9.5px] text-ink-500 italic">{t('node.noOptionEdges')}</p>
            </div>
            <Handle type="source" position={Position.Right} id="default" className="!w-[14px] !h-[14px] !rounded-[3px] !bg-white !border-[3px] !border-ink-700 !right-[-8px]" />
        </div>
        </>
    );
}

function EndNode({ id, data, selected }) {
    const { t } = useTranslation('wsSurvey');
    return (
        <>
        <NodeDeleteToolbar id={id} selected={selected} />
        <div
            className={[
                'bg-ink-900 text-cream-50 rounded-lg overflow-hidden transition-all min-w-[180px]',
                selected ? 'shadow-[0_0_0_3px_rgba(200,74,44,0.25)]' : 'shadow-md',
            ].join(' ')}
        >
            <Handle type="target" position={Position.Left} id="target" className="!w-[16px] !h-[16px] !rounded-full !bg-white !border-[3px] !border-ink-700 !left-[-8px]" />
            <div className="px-3 py-2.5">
                <div className="flex items-center gap-1.5 mb-1">
                    <PhoneOff size={12} strokeWidth={1.8} />
                    <span className="font-mono text-[11px] tracking-[0.18em]" style={{ fontWeight: 600 }}>
                        END
                    </span>
                </div>
                <p className="text-[13px]" style={{ fontFamily: '"Fraunces", "Noto Serif TC", serif', fontWeight: 500 }}>
                    {data.displayLabel || t('node.end')}
                </p>
            </div>
        </div>
        </>
    );
}

const NODE_TYPES = {
    question: QuestionNode,
    'random-group': RandomGroupNode,
    'reorder-group': ReorderGroupNode,
    end: EndNode,
};

// ── Context:讓自訂 edge / node 拿到 FlowCanvas 的 local setters ──
// (用 useReactFlow().set* 在 controlled mode 下不會同步,被刪的會被 props 蓋回)
const FlowActionsContext = createContext({ deleteEdge: () => {}, deleteNode: () => {}, dissolveGroup: () => {} });

// 共用:選中時浮在 node 上方的 ✕ 刪除按鈕(對齊 edge 的 ✕)
function NodeDeleteToolbar({ id, selected }) {
    const { t } = useTranslation('wsSurvey');
    const { deleteNode } = useContext(FlowActionsContext);
    return (
        <NodeToolbar isVisible={selected} position={Position.Top} offset={6}>
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); deleteNode(id); }}
                onMouseDown={(e) => e.stopPropagation()}
                className="nodrag nopan"
                title={t('toolbar.deleteNode')}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '3px 8px 3px 6px',
                    borderRadius: 11,
                    border: '2px solid #fbf8f1',
                    background: '#c84a2c',
                    color: '#fbf8f1',
                    fontSize: 10,
                    fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                    letterSpacing: '0.04em',
                    cursor: 'pointer',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
                    pointerEvents: 'all',
                }}
            >
                <X size={11} strokeWidth={2.8} />
                <span>{t('common:actions.delete')}</span>
            </button>
        </NodeToolbar>
    );
}

// Group node 專用:選中時浮兩顆按鈕(解散 + 刪除)
function GroupNodeToolbar({ id, selected }) {
    const { t } = useTranslation('wsSurvey');
    const { deleteNode, dissolveGroup } = useContext(FlowActionsContext);
    const baseBtn = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '3px 8px 3px 6px',
        borderRadius: 11,
        border: '2px solid #fbf8f1',
        color: '#fbf8f1',
        fontSize: 10,
        fontFamily: 'JetBrains Mono, ui-monospace, monospace',
        letterSpacing: '0.04em',
        cursor: 'pointer',
        boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
        pointerEvents: 'all',
    };
    return (
        <NodeToolbar isVisible={selected} position={Position.Top} offset={6}>
            <div style={{ display: 'inline-flex', gap: 6 }}>
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); dissolveGroup(id); }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="nodrag nopan"
                    title={t('toolbar.dissolveGroupTitle')}
                    style={{ ...baseBtn, background: '#5b4636' }}
                >
                    <Shuffle size={11} strokeWidth={2.4} />
                    <span>{t('toolbar.dissolve')}</span>
                </button>
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); deleteNode(id); }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="nodrag nopan"
                    title={t('toolbar.deleteGroupTitle')}
                    style={{ ...baseBtn, background: '#c84a2c' }}
                >
                    <X size={11} strokeWidth={2.8} />
                    <span>{t('common:actions.delete')}</span>
                </button>
            </div>
        </NodeToolbar>
    );
}

// ── Custom edge:選中後浮一個 ✕ 刪除按鈕(免按鍵盤)──
function DeletableEdge({
    id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition,
    style, markerEnd, selected, label, labelStyle, labelBgStyle, labelBgBorderRadius,
}) {
    const { t } = useTranslation('wsSurvey');
    const [edgePath, labelX, labelY] = getSmoothStepPath({
        sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition,
    });
    const { deleteEdge } = useContext(FlowActionsContext);

    const onDelete = (e) => {
        e.stopPropagation();
        e.preventDefault();
        deleteEdge(id);
    };

    return (
        <>
            <BaseEdge id={id} path={edgePath} markerEnd={markerEnd} style={style} />
            <EdgeLabelRenderer>
                {/* Label badge */}
                {label && (
                    <div
                        className="nodrag nopan"
                        style={{
                            position: 'absolute',
                            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
                            background: labelBgStyle?.fill || '#fbf8f1',
                            border: `1px solid ${labelBgStyle?.stroke || '#e0d4ba'}`,
                            borderRadius: labelBgBorderRadius || 3,
                            padding: '2px 5px',
                            pointerEvents: 'all',
                            ...labelStyle,
                        }}
                    >
                        {label}
                    </div>
                )}

                {/* Delete button(selected 時才出現)*/}
                {selected && (
                    <button
                        type="button"
                        onClick={onDelete}
                        onMouseDown={(e) => e.stopPropagation()}
                        className="nodrag nopan delete-edge-btn"
                        title={t('toolbar.deleteEdge')}
                        style={{
                            position: 'absolute',
                            transform: `translate(-50%, -50%) translate(${labelX + (label ? 38 : 0)}px, ${labelY}px)`,
                            width: 22,
                            height: 22,
                            borderRadius: 11,
                            border: '2px solid #fbf8f1',
                            background: '#c84a2c',
                            color: '#fbf8f1',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
                            pointerEvents: 'all',
                            zIndex: 10,
                        }}
                    >
                        <X size={12} strokeWidth={2.8} />
                    </button>
                )}
            </EdgeLabelRenderer>
        </>
    );
}

const EDGE_TYPES = {
    deletable: DeletableEdge,
};

// ── Layout: 橫向 left-to-right 排列 ─────────────────────────────
// skill 生的 position 都是 x=20 vertical chain → 對 option handle 在右側、
// target handle 在左側的設計來說,L→R 才是自然方向。
// 每 node 寬約 320px,間距 80px → x = i * 400。
// 同列稍微 stagger y(差 40px)讓 edge 彎得有變化,看起來不是死板水平線。
function autoLayout(nodes) {
    return nodes.map((n, i) => ({
        ...n,
        position: {
            x: i * 400,
            y: (i % 2) * 40,                     // 偶數列 y=0,奇數列 y=40
        },
    }));
}

// 判斷哪些節點是「孤立的」(沒任何 in/out)+ 哪些 target 沒被連
function annotateConnections(nodes, edges) {
    const incoming = new Set();
    const outgoing = new Set();
    edges.forEach((e) => { incoming.add(e.target); outgoing.add(e.source); });
    return nodes.map((n) => ({
        ...n,
        data: {
            ...n.data,
            hasIncoming: incoming.has(n.id),
            isIsolated: !incoming.has(n.id) && !outgoing.has(n.id),
        },
    }));
}

function buildRfGraph(flow, questionMap, tr) {
    const canvas = flow?.canvasFlow;
    if (!canvas?.nodes?.length) return { rfNodes: [], rfEdges: [] };

    // ── 自動清 orphan ──
    // 題庫刪題 / 重建 → canvas node 留下指向不存在 questionId 的孤兒 → 沒 options、無法拉線。
    // 載入時直接過濾掉,同步移除連到它們的 edges。non-question(end/group)不檢查。
    const validQuestionIds = new Set(Object.keys(questionMap || {}));
    const orphanNodeIds = new Set(
        canvas.nodes
            .filter((n) => n.type === 'question' && n.questionId && !validQuestionIds.has(n.questionId))
            .map((n) => n.id),
    );
    if (orphanNodeIds.size > 0) {
        console.warn(`[SurveyFlow] 過濾 ${orphanNodeIds.size} 個 orphan question node(questionId 對不到題庫)`);
    }
    const filteredCanvas = {
        ...canvas,
        nodes: canvas.nodes.filter((n) => !orphanNodeIds.has(n.id)),
        edges: (canvas.edges || []).filter((e) => !orphanNodeIds.has(e.source) && !orphanNodeIds.has(e.target)),
    };

    // skill 生的 chain 是 x=20 vertical → 用 staggered layout 重排
    // 真正 user 編輯過(任一 node x !== 20)就尊重原始位置
    const allAtSameX = filteredCanvas.nodes.every((n) => (n.position?.x ?? 20) === 20);

    const baseNodes = filteredCanvas.nodes.map((n) => {
        const q = n.questionId ? questionMap[n.questionId] : null;
        const options = !q?.useTemplate ? (q?.customOptions || []) : [];
        // 每題色:以 displayOrder 為基(question/group/end 都吃);end 統一 ink-900
        const c = n.type === 'end' ? { base: '#1c1a17', dark: '#1c1a17' } : colorForIndex(n.displayOrder);

        // group node:DB 只存 questionIds(避免題目改了 group 顯示沒同步),展開用的 questions[]
        // 在 buildRfGraph 從 questionMap 反查重組
        const groupQuestions = (n.type === 'random-group' || n.type === 'reorder-group')
            ? (n.questionIds || []).map((qid) => {
                const gq = questionMap[qid];
                return {
                    questionId: qid,
                    question: gq?.question || tr('node.questionDeleted'),
                    questionType: gq?.questionType || 'choice',
                    customOptions: gq?.customOptions || [],
                };
            })
            : undefined;

        return {
            id: n.id,
            type: n.type,
            position: n.position || { x: 0, y: 0 },
            draggable: true,                     // Phase 2:開放拖曳
            selectable: true,
            data: {
                displayOrder: n.displayOrder,
                displayLabel: n.displayLabel,
                questionId: n.questionId,
                questionText: n.questionText || q?.question || '',
                questionType: n.questionType || q?.questionType || 'choice',
                options,
                title: n.title,
                selectCount: n.selectCount,
                questionIds: n.questionIds,
                questions: groupQuestions,        // group 展開用
                colorBase: c.base,
                colorDark: c.dark,
            },
        };
    });

    const positioned = allAtSameX ? autoLayout(baseNodes) : baseNodes;

    const rfEdges = (filteredCanvas.edges || []).map((e) => styleSkillEdge({
        id: e.id,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle,
        targetHandle: e.targetHandle || 'target',
        data: {
            skillType: e.type || 'default',
            condition: e.condition,
            label: e.label,
        },
    }));

    const rfNodes = annotateConnections(positioned, rfEdges);
    return { rfNodes, rfEdges };
}

// ── Inner canvas:管 node drag + edge add/delete + 對外回報 dirty ──

function FlowCanvas({ initialNodes, initialEdges, questionMap, onCanvasChange }) {
    const { t } = useTranslation('wsSurvey');
    const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
    const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
    const { screenToFlowPosition } = useReactFlow();
    const [qMenuOpen, setQMenuOpen] = useState(false);
    // 復原 stack — 每次 build group / 重大破壞性動作前 push snapshot,「復原」按鈕 pop
    const [undoStack, setUndoStack] = useState([]);

    // 給 DeletableEdge / NodeDeleteToolbar 用 — 都改 LOCAL state(否則被 props 蓋回)
    const deleteEdge = useCallback((edgeId) => {
        setEdges((eds) => eds.filter((e) => e.id !== edgeId));
    }, [setEdges]);
    // 刪 node 時 cascade 把接在上面的所有 edges 也清掉
    // node 刪掉後若是 question type,該 questionId 會立刻從 unlinkedQuestions 排除集合中消失
    // → toolbar QUESTION 下拉自動把這題目重新列出來,user 可再加回去
    const deleteNode = useCallback((nodeId) => {
        setNodes((nds) => nds.filter((n) => n.id !== nodeId));
        setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
    }, [setNodes, setEdges]);
    // 解散群組:把 group node 拆回個別 question nodes,以 default edge 線性串接,
    // 入站邊改接第一題、出站邊改從最後一題出。push undoStack 可復原。
    const dissolveGroup = useCallback((groupId) => {
        const groupNode = nodes.find((n) => n.id === groupId);
        if (!groupNode) return;
        if (groupNode.type !== 'random-group' && groupNode.type !== 'reorder-group') return;

        const questionIds = groupNode.data?.questionIds || [];
        if (questionIds.length === 0) return;

        // snapshot
        setUndoStack((prev) => [...prev, {
            nodes: nodes.map((n) => ({ ...n })),
            edges: edges.map((e) => ({ ...e })),
            ts: Date.now(),
            action: t('action.dissolveGroup'),
        }].slice(-10));

        const baseX = groupNode.position?.x ?? 0;
        const baseY = groupNode.position?.y ?? 0;
        const maxOrder = nodes.reduce((m, n) => Math.max(m, n.data?.displayOrder ?? 0), 0);

        // 為每個 questionId 建 question node(對齊 buildRfGraph baseNodes 的 data shape:
        //  options / colorBase / colorDark / displayLabel 都要帶,否則 QuestionNode 渲染不出選項+標題色)
        const newQuestionNodes = questionIds.map((qid, idx) => {
            const q = questionMap?.[qid];
            const newId = `q-${qid}-${Date.now()}-${idx}`;
            const displayOrder = maxOrder + 1 + idx;
            const c = colorForIndex(displayOrder);
            const options = !q?.useTemplate ? (q?.customOptions || []) : [];
            return {
                id: newId,
                type: 'question',
                position: { x: baseX, y: baseY + idx * 130 },
                draggable: true,
                selectable: true,
                data: {
                    displayOrder,
                    displayLabel: q?.question?.slice(0, 20) || t('node.questionN', { n: idx + 1 }),
                    questionId: qid,
                    questionType: q?.questionType || 'choice',
                    questionText: q?.question || '',
                    options,
                    colorBase: c.base,
                    colorDark: c.dark,
                },
            };
        });

        const firstNewId = newQuestionNodes[0].id;
        const lastNewId = newQuestionNodes[newQuestionNodes.length - 1].id;

        // nodes: 移除 group + 加入個別題目
        setNodes((nds) => [
            ...nds.filter((n) => n.id !== groupId).map((n) => ({ ...n, selected: false })),
            ...newQuestionNodes.map((n, i) => ({ ...n, selected: i === 0 })),
        ]);

        // edges: 入站 → 接 first;出站 → 從 last 出;題目間串 default edge
        setEdges((eds) => {
            const rewired = [];
            for (const e of eds) {
                if (e.target === groupId) {
                    rewired.push({ ...e, target: firstNewId, targetHandle: undefined });
                } else if (e.source === groupId) {
                    rewired.push({ ...e, source: lastNewId, sourceHandle: undefined });
                } else {
                    rewired.push(e);
                }
            }
            // 題目間 default 串接 (q1 → q2 → q3 ...),過 styleSkillEdge 拿到 stroke + arrow marker
            for (let i = 0; i < newQuestionNodes.length - 1; i++) {
                rewired.push(styleSkillEdge({
                    id: `e-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 6)}`,
                    source: newQuestionNodes[i].id,
                    target: newQuestionNodes[i + 1].id,
                    sourceHandle: undefined,
                    targetHandle: 'target',
                    data: { skillType: 'default' },
                }));
            }
            return rewired;
        });
    }, [nodes, edges, setNodes, setEdges, questionMap, t]);

    const flowActions = useMemo(() => ({ deleteEdge, deleteNode, dissolveGroup }), [deleteEdge, deleteNode, dissolveGroup]);

    // 「未在流程中」的題目(canvasFlow 沒有 node 引用該 questionId)
    const unlinkedQuestions = useMemo(() => {
        if (!questionMap) return [];
        const linkedIds = new Set(nodes.filter((n) => n.data?.questionId).map((n) => n.data.questionId));
        return Object.values(questionMap)
            .filter((q) => !linkedIds.has(q.id))
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }, [nodes, questionMap]);

    // 算「新節點落點」— viewport 左上(toolbar 旁,user 看得到)
    // toolbar 在 top-left 大約 (12, 12) 起,寬 ~170px → 新節點放在 toolbar 右側 ~200px
    const computeDropPos = useCallback(() => {
        const canvasEl = document.querySelector('.react-flow');
        const rect = canvasEl?.getBoundingClientRect() || { left: 0, top: 0 };
        return screenToFlowPosition({
            x: rect.left + 220,   // 距 canvas 左邊 220px(toolbar 右側一點)
            y: rect.top + 80,     // 距 canvas 上邊 80px(toolbar 下面一點)
        });
    }, [screenToFlowPosition]);

    // 初始 positions 拿來做 dirty diff
    const initialPosMap = useMemo(() => {
        const m = {};
        initialNodes.forEach((n) => { m[n.id] = { x: n.position.x, y: n.position.y }; });
        return m;
    }, [initialNodes]);
    const initialNodeIds = useMemo(() => new Set(initialNodes.map((n) => n.id)), [initialNodes]);
    const initialEdgeIds = useMemo(() => new Set(initialEdges.map((e) => e.id)), [initialEdges]);

    // 每次 nodes / edges 變動 → 算 dirty + 回報給父層
    useEffect(() => {
        const positionsDirty = nodes.some((n) => {
            const init = initialPosMap[n.id];
            return !init || init.x !== n.position.x || init.y !== n.position.y;
        });
        const nodesAddRemoveDirty =
            nodes.length !== initialNodes.length
            || nodes.some((n) => !initialNodeIds.has(n.id))
            || initialNodes.some((n) => !nodes.find((x) => x.id === n.id));
        const edgesDirty =
            edges.length !== initialEdges.length
            || edges.some((e) => !initialEdgeIds.has(e.id))
            || initialEdges.some((e) => !edges.find((x) => x.id === e.id));
        const positions = {};
        nodes.forEach((n) => { positions[n.id] = { x: n.position.x, y: n.position.y }; });
        onCanvasChange?.({
            dirty: positionsDirty || edgesDirty || nodesAddRemoveDirty,
            positions,
            nodes,
            edges,
        });
    }, [nodes, edges, initialPosMap, initialNodeIds, initialEdgeIds, initialNodes, initialEdges, onCanvasChange]);

    // 父層按「還原版面」→ 把 nodes/edges 拍回初始
    useEffect(() => {
        setNodes(initialNodes);
        setEdges(initialEdges);
    }, [initialNodes, initialEdges, setNodes, setEdges]);

    // 新增 END 節點 — 放在 viewport 左上(toolbar 旁,user 看得到、好拖)
    const addEndNode = useCallback(() => {
        const pos = computeDropPos();
        const existingEnds = nodes.filter((n) => n.type === 'end').length;
        const id = `end-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        const newNode = {
            id,
            type: 'end',
            position: pos,
            draggable: true,
            selectable: true,
            data: {
                displayLabel: existingEnds === 0 ? t('node.end') : t('node.endN', { n: existingEnds + 1 }),
                displayOrder: 999 + existingEnds,
            },
        };
        setNodes((nds) => [...nds, newNode]);
    }, [nodes, setNodes, computeDropPos, t]);

    // 新增 QUESTION 節點 — 從 unlinked 題目挑一個放上 canvas,位置同 END
    const addQuestionNode = useCallback((q) => {
        if (!q) return;
        setQMenuOpen(false);
        const pos = computeDropPos();
        // 算 displayOrder = 既有最大 + 1(讓顏色 palette 自然延伸)
        const maxOrder = nodes.reduce((m, n) => Math.max(m, n.data?.displayOrder ?? 0), 0);
        const id = `n-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        const c = colorForIndex(maxOrder + 1);
        const options = !q.useTemplate ? (q.customOptions || []) : [];
        const newNode = {
            id,
            type: 'question',
            position: pos,
            draggable: true,
            selectable: true,
            data: {
                displayOrder: maxOrder + 1,
                displayLabel: `Q${maxOrder + 1}`,
                questionId: q.id,
                questionText: q.question || '',
                questionType: q.questionType || 'choice',
                options,
                colorBase: c.base,
                colorDark: c.dark,
                hasIncoming: false,
                isIsolated: true,        // 剛加上去還沒接線
            },
        };
        setNodes((nds) => [...nds, newNode]);
    }, [nodes, setNodes, computeDropPos]);

    // ── 群組節點建立(對齊 Vue editor createRandomGroup pattern)──
    // 1) snapshot 進 undoStack(可復原)
    // 2) 計算 questions[] 含 question text + customOptions
    // 3) 新節點 position = 選中 nodes 的左上角 (minX, minY)
    // 4) 從 nodes 移除原 question nodes
    // 5) edges rewire:
    //    - 入站(外部 → 被選中)→ target = group.id, targetHandle='target'
    //    - 出站(被選中 → 外部):有 condition 的(option-N → other)保留;沒 condition 的 default 刪掉
    //    - 內部(被選中 → 被選中)→ 直接刪
    const buildGroupNode = useCallback((type, selectedNodes, extraData = {}) => {
        if (!selectedNodes.length) return;

        // 1) snapshot 先存,失敗也可復原
        setUndoStack((prev) => [...prev, {
            nodes: nodes.map((n) => ({ ...n })),
            edges: edges.map((e) => ({ ...e })),
            ts: Date.now(),
            action: type === 'random-group' ? t('action.createRandomGroup') : t('action.createReorderGroup'),
        }].slice(-10));   // 最多保留 10 步

        const sourceIds = new Set(selectedNodes.map((n) => n.id));
        const maxOrder = nodes.reduce((m, n) => Math.max(m, n.data?.displayOrder ?? 0), 0);
        const groupLabel = type === 'random-group' ? t('node.randomGroup') : t('node.reorderGroupShort');

        // 算 questions[]
        const questions = selectedNodes.map((n) => {
            const qid = n.data?.questionId;
            const q = qid ? questionMap[qid] : null;
            return {
                questionId: qid,
                question: q?.question || n.data?.questionText || t('node.unsetQuestion'),
                questionType: q?.questionType || n.data?.questionType || 'choice',
                customOptions: q?.customOptions || [],
            };
        });
        const questionIds = questions.map((q) => q.questionId).filter(Boolean);

        // position = 左上角
        const minX = Math.min(...selectedNodes.map((n) => n.position?.x ?? 0));
        const minY = Math.min(...selectedNodes.map((n) => n.position?.y ?? 0));

        const id = `${type === 'random-group' ? 'rg' : 'og'}-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`;
        const newNode = {
            id,
            type,
            position: { x: minX, y: minY },
            draggable: true,
            selectable: true,
            data: {
                displayOrder: maxOrder + 1,
                displayLabel: groupLabel,
                title: extraData.title || groupLabel,
                questionIds,
                questions,
                ...(type === 'random-group' ? { selectCount: Math.min(extraData.selectCount || 1, questions.length) } : {}),
            },
        };

        // 2) nodes: 移除原 + 加 group + deselect all + select group(panel 自動消失)
        setNodes((nds) => [
            ...nds.filter((n) => !sourceIds.has(n.id)).map((n) => ({ ...n, selected: false })),
            { ...newNode, selected: true },
        ]);

        // 3) edges rewire
        setEdges((eds) => {
            const out = [];
            for (const e of eds) {
                const srcInGroup = sourceIds.has(e.source);
                const tgtInGroup = sourceIds.has(e.target);
                if (srcInGroup && tgtInGroup) continue;   // 內部:丟
                if (tgtInGroup) {                         // 入站 → group
                    out.push({ ...e, target: id, targetHandle: 'target' });
                    continue;
                }
                if (srcInGroup) {                          // 出站
                    const isConditional = e.data?.skillType === 'conditional' || !!e.data?.condition;
                    if (type === 'reorder-group') {
                        // reorder 不支援條件分支 → 一律刪
                        continue;
                    }
                    if (!isConditional) continue;          // random + default → 刪(用 group 預設出邊取代)
                    // random + conditional → 保留,改成從 group 出發
                    // sourceHandle 對應原 option 還在 — 但 group node 沒這個 handle id;
                    // 簡化:把 sourceHandle 改成 default(MVP),user 之後可重連
                    out.push({ ...e, source: id, sourceHandle: 'default' });
                    continue;
                }
                out.push(e);                                // 外部邊:不動
            }
            return out;
        });
    }, [nodes, edges, setNodes, setEdges, questionMap, t]);

    // 復原 — pop undoStack 還原最近一個快照
    const handleUndo = useCallback(() => {
        if (!undoStack.length) return;
        const last = undoStack[undoStack.length - 1];
        setNodes(last.nodes);
        setEdges(last.edges);
        setUndoStack((prev) => prev.slice(0, -1));
    }, [undoStack, setNodes, setEdges]);

    // 拉新 edge — 從 option-N handle 拉到別的 node 的 target handle
    const onConnect = useCallback((conn) => {
        if (!conn.source || !conn.target) return;
        const sourceNode = nodes.find((n) => n.id === conn.source);
        const sh = conn.sourceHandle;
        let condition;
        let skillType = 'default';
        // 從 option-N handle 出來 → 條件邊,自動填 answerValue/answerLabel
        if (sh && sh.startsWith('option-')) {
            const idx = parseInt(sh.replace('option-', ''), 10);
            const opt = sourceNode?.data?.options?.[idx];
            if (opt) {
                condition = { answerValue: opt.value || `option-${idx}`, answerLabel: opt.label };
                skillType = 'conditional';
            }
        }
        const id = `e-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        const newEdge = styleSkillEdge({
            id,
            source: conn.source,
            target: conn.target,
            sourceHandle: sh || undefined,
            targetHandle: conn.targetHandle || 'target',
            data: { skillType, condition, label: condition?.answerLabel },
        });
        setEdges((eds) => [...eds, newEdge]);
    }, [nodes, setEdges]);

    return (
        <FlowActionsContext.Provider value={flowActions}>
        <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            nodeTypes={NODE_TYPES}
            edgeTypes={EDGE_TYPES}
            // ★ 初始 viewport — 不 fitView 全部(會 zoom 太小),從第一題開始橫向看
            defaultViewport={{ x: 40, y: 80, zoom: 0.75 }}
            minZoom={0.15}
            maxZoom={1.8}
            proOptions={{ hideAttribution: true }}
            nodesDraggable={true}
            nodesConnectable={true}              // ★ Phase 2:可拉新 edge
            edgesUpdatable={true}
            elementsSelectable
            deleteKeyCode={['Delete', 'Backspace']}  // ★ 選 edge 後按 delete 刪除
            panOnDrag
            zoomOnScroll
            connectionLineStyle={{ stroke: '#c84a2c', strokeWidth: 2, strokeDasharray: '5,3' }}
        >
            <Background gap={24} size={1.2} color="#e0d4ba" />
            <Controls
                showInteractive={false}
                className="!bg-cream-50 !border !border-cream-300 !rounded-md !shadow-md"
            />
            {/* 復原 button — 任何 destructive 動作後出現,讓 user 拆回上一步 */}
            {undoStack.length > 0 && (
                <Panel position="bottom-left">
                    <button
                        onClick={handleUndo}
                        className="inline-flex items-center gap-1.5 px-3 py-2 mb-2 ml-2 rounded-md bg-cream-50 border border-cream-300 shadow-md hover:bg-white hover:border-accent-500 text-[12px] text-ink-900 transition-all"
                        title={t('undo.title', { action: undoStack[undoStack.length - 1]?.action || t('undo.lastStep') })}
                    >
                        <RotateCcw size={12} strokeWidth={1.8} className="text-accent-700" />
                        <span>{t('undo.label')}</span>
                        <span className="font-mono text-[10px] text-ink-400 tracking-wider">
                            {undoStack[undoStack.length - 1]?.action || t('undo.lastStep')}
                        </span>
                    </button>
                </Panel>
            )}

            {/* 多選 question nodes ≥2 → 浮動建組 panel(編輯風,對齊 frontend-design SKILL)*/}
            {(() => {
                const selectedQNodes = nodes.filter((n) => n.selected && n.type === 'question' && n.data?.questionId);
                if (selectedQNodes.length < 2) return null;
                // 預設抽 1 題(可在 node 本身編輯 selectCount,不彈視窗打擾)
                const onMakeRandom = () => buildGroupNode('random-group', selectedQNodes, { selectCount: 1 });
                const onMakeReorder = () => buildGroupNode('reorder-group', selectedQNodes);
                return (
                    <Panel position="bottom-right">
                        <div className="bg-cream-50 border border-cream-300 rounded-lg shadow-xl px-5 py-4 w-[380px] mb-2 mr-2">
                            {/* Editorial header */}
                            <div className="flex items-baseline justify-between gap-3 mb-3">
                                <div>
                                    <div
                                        className="font-mono text-[10px] tracking-[0.2em] uppercase text-accent-700 mb-0.5"
                                        style={{ fontWeight: 600 }}
                                    >
                                        Selection
                                    </div>
                                    <h3
                                        className="text-[15px] text-ink-900 leading-tight"
                                        style={{ fontFamily: '"Fraunces", "Noto Serif TC", serif', fontWeight: 500 }}
                                    >
                                        <Trans
                                            i18nKey="selection.selected"
                                            ns="wsSurvey"
                                            count={selectedQNodes.length}
                                            components={{ n: <span className="font-mono tabular-nums text-accent-700" /> }}
                                        />
                                    </h3>
                                </div>
                                <span className="font-mono text-[9px] tracking-[0.18em] uppercase text-ink-300">
                                    Group · build
                                </span>
                            </div>

                            {/* 2 action cards — editorial,不用 generic SaaS 紫橘 */}
                            <div className="grid grid-cols-2 gap-2">
                                {/* 預設兩張卡都中性,hover 才上色 — 避免被誤判已選 */}
                                <button
                                    onClick={onMakeRandom}
                                    className="group text-left p-3 rounded-md border-2 border-cream-300 bg-white hover:border-accent-500 hover:bg-accent-50 hover:-translate-y-px transition-all"
                                >
                                    <div className="flex items-center gap-1.5 mb-1.5">
                                        <Shuffle size={12} className="text-ink-500 group-hover:text-accent-700" strokeWidth={2} />
                                        <span
                                            className="font-mono text-[9px] tracking-[0.18em] text-ink-500 group-hover:text-accent-700 transition-colors"
                                            style={{ fontWeight: 600 }}
                                        >
                                            RANDOM SELECT
                                        </span>
                                    </div>
                                    <div
                                        className="text-[13px] text-ink-900 mb-1"
                                        style={{ fontFamily: '"Fraunces", "Noto Serif TC", serif', fontWeight: 500 }}
                                    >
                                        {t('selection.randomCard.title')}
                                    </div>
                                    <p className="text-[10.5px] text-ink-500 leading-snug">
                                        {t('selection.randomCard.desc', { count: selectedQNodes.length })}
                                    </p>
                                </button>

                                <button
                                    onClick={onMakeReorder}
                                    className="group text-left p-3 rounded-md border-2 border-cream-300 bg-white hover:border-ink-900 hover:bg-cream-50 hover:-translate-y-px transition-all"
                                >
                                    <div className="flex items-center gap-1.5 mb-1.5">
                                        <ArrowDownNarrowWide size={12} className="text-ink-500 group-hover:text-ink-900" strokeWidth={2} />
                                        <span
                                            className="font-mono text-[9px] tracking-[0.18em] text-ink-500 group-hover:text-ink-900 transition-colors"
                                            style={{ fontWeight: 600 }}
                                        >
                                            REORDER ALL
                                        </span>
                                    </div>
                                    <div
                                        className="text-[13px] text-ink-900 mb-1"
                                        style={{ fontFamily: '"Fraunces", "Noto Serif TC", serif', fontWeight: 500 }}
                                    >
                                        {t('selection.reorderCard.title')}
                                    </div>
                                    <p className="text-[10.5px] text-ink-500 leading-snug">
                                        {t('selection.reorderCard.desc')}
                                    </p>
                                </button>
                            </div>

                            <p className="font-mono text-[9px] tracking-wider uppercase text-ink-400 mt-3 leading-snug">
                                {t('selection.hint')}
                            </p>
                        </div>
                    </Panel>
                );
            })()}

            {/* Toolbar — 新增節點 */}
            <Panel position="top-left">
                <div className="flex flex-col gap-2 bg-cream-50/95 backdrop-blur border border-cream-300 rounded-md shadow-md p-2 w-[170px]">
                    <div
                        className="font-mono text-[9px] tracking-[0.18em] uppercase text-ink-400 pl-1"
                        style={{ fontWeight: 600 }}
                    >
                        Add node
                    </div>

                    {/* + 問題節點 — 下拉選 unlinked questions */}
                    <div className="relative">
                        <button
                            onClick={() => setQMenuOpen((v) => !v)}
                            title={unlinkedQuestions.length ? t('toolbar.unlinkedCount', { count: unlinkedQuestions.length }) : t('toolbar.allLinked')}
                            className="w-full flex items-center justify-between gap-1.5 px-2.5 py-1.5 text-[11.5px] rounded border border-accent-500 bg-accent-50 hover:bg-accent-100 text-accent-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            disabled={unlinkedQuestions.length === 0}
                        >
                            <span className="flex items-center gap-1.5">
                                <ListChecks size={11} strokeWidth={1.8} />
                                <span className="font-mono tracking-wider">QUESTION</span>
                            </span>
                            <span className="flex items-center gap-1">
                                <span className="font-mono tabular-nums text-[10px]">
                                    {unlinkedQuestions.length}
                                </span>
                                <ChevronDown size={10} strokeWidth={2} className={qMenuOpen ? 'rotate-180 transition-transform' : 'transition-transform'} />
                            </span>
                        </button>

                        {qMenuOpen && unlinkedQuestions.length > 0 && (
                            <div className="absolute top-full left-0 mt-1 w-[280px] max-h-[320px] overflow-y-auto bg-cream-50 border border-cream-300 rounded-md shadow-lg z-20">
                                <div
                                    className="px-2.5 py-1.5 border-b border-cream-200 font-mono text-[9px] tracking-[0.16em] uppercase text-ink-400"
                                    style={{ fontWeight: 600 }}
                                >
                                    {t('toolbar.unlinkedTitle')}
                                </div>
                                <ul className="py-1">
                                    {unlinkedQuestions.map((q) => (
                                        <li key={q.id}>
                                            <button
                                                onClick={() => addQuestionNode(q)}
                                                className="w-full text-left px-2.5 py-1.5 hover:bg-cream-100 transition-colors flex items-start gap-2"
                                            >
                                                <span className="font-mono text-[9px] text-ink-400 tabular-nums w-5 text-right pt-0.5">
                                                    {String(q.order || '?').padStart(2, '0')}
                                                </span>
                                                <span className="flex-1 text-[11.5px] text-ink-900 leading-snug line-clamp-2">
                                                    {q.question || t('node.noQuestionText')}
                                                </span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

                    {/* + END */}
                    <button
                        onClick={addEndNode}
                        title={t('toolbar.addEndTitle')}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11.5px] rounded border border-ink-900 bg-ink-900 hover:bg-ink-700 text-cream-50 transition-colors"
                    >
                        <PhoneOff size={11} strokeWidth={1.8} />
                        <span className="font-mono tracking-wider">END</span>
                    </button>
                </div>
            </Panel>
        </ReactFlow>
        </FlowActionsContext.Provider>
    );
}

// 把 skill 格式 edge 套上 xyflow 視覺(stroke / marker / label),共用給初始載入跟 onConnect
function styleSkillEdge(e) {
    const isConditional = e.data?.skillType === 'conditional' || !!e.data?.condition;
    return {
        ...e,
        type: 'deletable',                       // 自訂 edge type — selected 時浮 ✕ 刪除按鈕
        label: e.data?.condition?.answerLabel || e.data?.label,
        labelStyle: {
            fontFamily: 'JetBrains Mono, ui-monospace, monospace',
            fontSize: 10,
            fill: isConditional ? '#a83a1f' : '#6e6358',
        },
        labelBgStyle: { fill: '#fbf8f1', fillOpacity: 0.95, stroke: '#e0d4ba' },
        labelBgPadding: [4, 2],
        labelBgBorderRadius: 3,
        style: {
            stroke: isConditional ? '#c84a2c' : '#a89e90',
            strokeWidth: isConditional ? 1.8 : 1.3,
        },
        markerEnd: {
            type: MarkerType.ArrowClosed,
            color: isConditional ? '#c84a2c' : '#a89e90',
            width: 14,
            height: 14,
        },
        animated: false,
    };
}

// ── Main modal ───────────────────────────────────────────────────

export function SurveyFlowEditorModal({ open, onClose, avatarId, avatarName }) {
    const { t } = useTranslation('wsSurvey');
    const [flow, setFlow] = useState(null);
    const [questionMap, setQuestionMap] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [resetTick, setResetTick] = useState(0);     // 變一次 → 觸發 FlowCanvas useEffect reset
    const positionsRef = useRef({});                    // 最新 positions(從 FlowCanvas 回報)
    const nodesRef = useRef([]);                        // 最新 xyflow nodes(含新增的)
    const edgesRef = useRef([]);                        // 最新 edges(含 add/delete)

    const loadAll = useCallback(async () => {
        if (!avatarId) return;
        setLoading(true);
        setError(null);
        try {
            const [flowRes, qRes] = await Promise.all([fetchFlow(avatarId), fetchQuestions(avatarId)]);
            const flowCfg = flowRes?.data || flowRes;
            const qs = qRes?.data || qRes?.questions || qRes || [];
            const map = {};
            (Array.isArray(qs) ? qs : []).forEach((q) => { map[q.id] = q; });
            setFlow(flowCfg);
            setQuestionMap(map);
            setDirty(false);
        } catch (e) {
            setError(e.message || t('error.loadFailed'));
        } finally {
            setLoading(false);
        }
    }, [avatarId, t]);

    useEffect(() => {
        if (open) loadAll();
        else { setFlow(null); setQuestionMap({}); setDirty(false); }
    }, [open, loadAll]);

    const { rfNodes, rfEdges } = useMemo(
        () => buildRfGraph(flow, questionMap, t),
        // resetTick 加進 deps 強制 rebuild → FlowCanvas 收到「新」initialNodes 會重置
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [flow, questionMap, resetTick, t],
    );

    // FlowCanvas 回報 dirty / positions / nodes / edges
    const handleCanvasChange = useCallback(({ dirty: d, positions, nodes, edges }) => {
        positionsRef.current = positions;
        nodesRef.current = nodes || [];
        edgesRef.current = edges;
        setDirty(d);
    }, []);

    // 還原版面 → bump resetTick,FlowCanvas useEffect 會把 nodes 拍回初始
    const handleReset = () => {
        setResetTick((t) => t + 1);
        setDirty(false);
    };

    // X / backdrop:dirty 時跳 confirm,否則靜默關
    const handleSilentClose = useCallback(() => {
        if (saving) return;
        if (dirty && !confirm(t('confirm.unsavedClose'))) return;
        onClose();
    }, [saving, dirty, onClose, t]);

    // 「儲存」 → 把現在的 xyflow nodes/edges 轉回 skill 格式 PUT 回後端 + 關 modal
    // 沒 dirty 直接關(避免 user 沒改卻按存還 round-trip)
    const handleSave = async () => {
        if (saving) return;
        if (!dirty || !flow?.canvasFlow) {
            onClose();
            return;
        }
        setSaving(true);
        try {
            const origNodes = flow.canvasFlow.nodes || [];
            // 走 xyflow 當前 nodes 集合:既有的更新 position,新增的從 data 組 skill 格式;
            // 既有 origNodes 中沒出現的 → 被使用者刪掉,自動丟棄
            const updatedNodes = (nodesRef.current || []).map((n) => {
                const orig = origNodes.find((o) => o.id === n.id);
                if (orig) {
                    return { ...orig, position: { x: n.position.x, y: n.position.y } };
                }
                // 新節點:從 xyflow data 組回 skill 格式
                const base = {
                    id: n.id,
                    type: n.type,
                    position: { x: n.position.x, y: n.position.y },
                    displayLabel: n.data?.displayLabel,
                    displayOrder: n.data?.displayOrder ?? 999,
                };
                if (n.type === 'question') {
                    if (n.data?.questionId) base.questionId = n.data.questionId;
                    if (n.data?.questionType) base.questionType = n.data.questionType;
                    if (n.data?.questionText) base.questionText = n.data.questionText;
                } else if (n.type === 'random-group' || n.type === 'reorder-group') {
                    base.title = n.data?.title || (n.type === 'random-group' ? t('node.randomGroup') : t('node.reorderGroupLong'));
                    base.questionIds = n.data?.questionIds || [];
                    if (n.type === 'random-group') base.selectCount = n.data?.selectCount ?? 1;
                }
                return base;
            });
            // xyflow edge → skill edge:刪掉 style/marker 那些渲染欄位
            const updatedEdges = (edgesRef.current || []).map((e) => ({
                id: e.id,
                source: e.source,
                target: e.target,
                sourceHandle: e.sourceHandle,
                targetHandle: e.targetHandle,
                type: e.data?.skillType || 'default',
                ...(e.data?.condition ? { condition: e.data.condition } : {}),
                ...(e.data?.label ? { label: e.data.label } : {}),
            }));
            // 從當前 random-group nodes 重新派生 randomGroups array
            // (後端 SurveyGeminiController.selectRandomFromGroup 用這個查 questionIds)
            const updatedRandomGroups = updatedNodes
                .filter((n) => n.type === 'random-group')
                .map((n) => ({
                    id: n.id,
                    title: n.title || n.data?.title || t('node.randomGroup'),
                    questionIds: n.questionIds || n.data?.questionIds || [],
                    selectCount: n.selectCount ?? n.data?.selectCount ?? 1,
                }));
            const payload = {
                avatarId,
                mode: flow.mode || 'canvas',
                simpleFlow: flow.simpleFlow,
                canvasFlow: {
                    ...flow.canvasFlow,
                    nodes: updatedNodes,
                    edges: updatedEdges,
                    randomGroups: updatedRandomGroups,
                },
            };
            await saveFlow(avatarId, payload);
            onClose();    // 儲存後直接關 modal(對齊題目編輯器的 pattern)
            return;
            // (以下 dirty 重置邏輯保留 — 萬一改成「存後不關」可重用)
            // 更新本地 flow → 下次 dirty diff 對齊新基準
            setFlow((f) => ({
                ...f,
                canvasFlow: { ...f.canvasFlow, nodes: updatedNodes, edges: updatedEdges },
            }));
            setDirty(false);
        } catch (e) {
            alert(t('error.saveFailed', { msg: e.message || e }));
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    const isCanvasMode = flow?.mode === 'canvas';
    const isSimpleMode = flow?.mode === 'simple';
    const nodeCount = flow?.canvasFlow?.nodes?.length || 0;
    const isolatedCount = rfNodes.filter((n) => n.data?.isIsolated).length;

    return (
        <div
            className="fixed inset-0 z-50 bg-ink-900/50 backdrop-blur-sm grid place-items-center p-4"
            {...overlayDismiss(handleSilentClose)}
        >
            <div
                className="bg-cream-50 rounded-lg shadow-2xl w-full max-w-7xl h-[92vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* ── Header ── */}
                <header className="px-7 py-4 border-b border-cream-200 flex items-start justify-between">
                    <div>
                        <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-accent-700 mb-1">
                            Survey / Flow {isCanvasMode && '· canvas'}{isSimpleMode && '· simple'}
                        </div>
                        <h2
                            className="text-2xl text-ink-900 leading-tight"
                            style={{ fontFamily: '"Fraunces", "Noto Serif TC", serif', fontWeight: 500 }}
                        >
                            {t('flow.title')}
                        </h2>
                        {avatarName && (
                            <p className="text-[12px] text-ink-500 mt-0.5">
                                {avatarName}
                                <span className="text-ink-300 mx-1">·</span>
                                <span className="font-mono text-[10px] tabular-nums">
                                    {t('flow.nodeCount', { count: nodeCount })}
                                </span>
                                {isolatedCount > 0 && (
                                    <>
                                        <span className="text-ink-300 mx-1">·</span>
                                        <span className="font-mono text-[10px] tabular-nums text-accent-700">
                                            {t('flow.isolatedCount', { count: isolatedCount })}
                                        </span>
                                    </>
                                )}
                                {dirty && (
                                    <>
                                        <span className="text-ink-300 mx-1">·</span>
                                        <span className="font-mono text-[10px] text-accent-700 tracking-widest uppercase">
                                            {t('flow.unsavedLayout')}
                                        </span>
                                    </>
                                )}
                            </p>
                        )}
                    </div>
                    <button
                        onClick={handleSilentClose}
                        className="text-ink-400 hover:text-ink-900 transition-colors -mt-1"
                        title={t('flow.closeTitle')}
                    >
                        <X size={18} />
                    </button>
                </header>

                {/* ── Canvas body ── */}
                <div className="flex-1 relative bg-cream-100/40 overflow-hidden">
                    {loading && (
                        <div className="absolute inset-0 z-10 grid place-items-center text-ink-400">
                            <div className="flex items-center gap-2 text-sm">
                                <Loader2 size={16} className="animate-spin" /> {t('flow.loading')}
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="absolute inset-0 z-10 grid place-items-center">
                            <div className="flex items-start gap-2 max-w-md border border-accent-300 bg-accent-50 text-accent-700 text-sm rounded-md px-4 py-3">
                                <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                                <span>{error}</span>
                            </div>
                        </div>
                    )}

                    {!loading && !error && rfNodes.length === 0 && (
                        <div className="absolute inset-0 z-10 grid place-items-center text-center px-6">
                            <div>
                                <Workflow size={28} className="mx-auto text-accent-500/40" strokeWidth={1.4} />
                                <h3
                                    className="text-lg text-ink-700 mt-3 mb-1"
                                    style={{ fontFamily: '"Fraunces", "Noto Serif TC", serif' }}
                                >
                                    {t('flow.emptyTitle')}
                                </h3>
                                <p className="text-[12px] text-ink-500 max-w-xs mx-auto leading-relaxed">
                                    {t('flow.emptyHint')}
                                </p>
                            </div>
                        </div>
                    )}

                    <ReactFlowProvider>
                        <FlowCanvas
                            initialNodes={rfNodes}
                            initialEdges={rfEdges}
                            questionMap={questionMap}
                            onCanvasChange={handleCanvasChange}
                        />
                    </ReactFlowProvider>
                </div>

                {/* ── Footer ── */}
                <footer className="px-7 py-2.5 border-t border-cream-200 flex items-center justify-between bg-cream-50">
                    <div className="flex items-center gap-4 font-mono text-[10px] tracking-[0.15em] uppercase text-ink-400">
                        <span className="inline-flex items-center gap-1.5">
                            <span className="inline-block w-2 h-2 rounded-full border-2 border-accent-500 bg-white"></span>
                            {t('legend.conditional')}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <span className="inline-block w-2 h-2 rounded-full border-2 border-ink-700 bg-white"></span>
                            {t('legend.default')}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <span className="inline-block w-2 h-2 rounded-[2px] border-2 border-accent-500 bg-white"></span>
                            {t('legend.optionExit')}
                        </span>
                        <span className="text-ink-500">{t('legend.hint')}</span>
                    </div>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        title={dirty ? t('flow.saveTitleDirty') : t('flow.saveTitleClean')}
                        className="px-4 py-1.5 text-[13px] rounded-md bg-ink-900 hover:bg-ink-700 text-cream-50 transition-colors disabled:opacity-60 disabled:cursor-wait"
                    >
                        {saving ? t('saving') : t('common:actions.save')}
                    </button>
                </footer>
            </div>
        </div>
    );
}
